"use client";

import type { HTMLAttributes, ReactNode, FC, Ref } from "react";

import type { ClassNameProp } from "../../props";
import { Flex } from "../Flex";
import { UploadCard, type UploadCardProps } from "../UploadCard";
import { FileRow } from "./components";
import { Tooltip } from "../Tooltip";
import { Icon } from "../Icon";
import { Text } from "../Text";

import { uploadCardStyle, trailingStyle } from "./OCRUploadCard.css";

/**
 * A single progress step with label and status
 */
export type OCRProgressStep = {
  label: string;
  status: "pending" | "in-progress" | "completed" | "error";
};

/**
 * Represents an uploaded file with its OCR processing status
 */
export type OCRUploadedFile = {
  /**
   * Unique identifier for the file
   */
  id: string;
  /**
   * The file name to display
   */
  name: string;
  /**
   * Overall status of the file
   */
  status: "in-progress" | "success" | "error";
  /**
   * Dynamic progress steps from backend
   */
  steps?: OCRProgressStep[];
  /**
   * Error message to display when status is "error"
   */
  errorMessage?: string;
};

export type OCRUploadCardProps = HTMLAttributes<HTMLDivElement> &
  ClassNameProp &
  Pick<
    UploadCardProps,
    "title" | "onUpload" | "acceptedTypes" | "disabled" | "loading"
  > & {
    /**
     * Description text for the upload card
     */
    description?: string;
    /**
     * Files with OCR processing status to display below the upload area
     */
    uploadedFiles?: OCRUploadedFile[];
    /**
     * Callback when view button is clicked
     */
    onView?: (fileId: string) => void;
    /**
     * Callback when delete button is clicked
     */
    onDelete?: (fileId: string) => void;
    /**
     * Callback when retry button is clicked
     */
    onRetry?: (fileId: string) => void;
    /**
     * Optional trailing content rendered top-right of the card
     * (e.g. an AI help button)
     */
    trailing?: ReactNode;
    /**
     * Whether to disable the tooltip
     */
    disableTooltip?: boolean;
    /**
     * Ref forwarded to the root element
     */
    ref?: Ref<HTMLDivElement>;
  };

/**
 * A card for uploading files with OCR processing status display.
 * Shows upload progress through three stages: Upload, Analyse, and Data plaatsen.
 */
export const OCRUploadCard: FC<OCRUploadCardProps> = ({
  title,
  description,
  onUpload,
  acceptedTypes,
  disabled,
  loading,
  uploadedFiles,
  onView,
  onDelete,
  onRetry,
  trailing,
  disableTooltip = false,
  className,
  style,
  ref,
  ...props
}) => {
  const hasUploadedFiles = uploadedFiles && uploadedFiles.length > 0;

  return (
    <Flex
      ref={ref}
      direction="column"
      gap="s"
      width="stretch"
      className={className}
      {...props}
    >
      <Tooltip
        value={
          !(disabled || hasUploadedFiles) &&
          !disableTooltip && (
            <Flex direction="row" gap="xs" align="center">
              <Icon icon="aiFilled" />
              <Text variant="bodyS">
                Vul het formulier automatisch in met behulp van AI
              </Text>
            </Flex>
          )
        }
      >
        <UploadCard
          title={title}
          body={description}
          onUpload={onUpload}
          acceptedTypes={acceptedTypes}
          disabled={disabled || hasUploadedFiles}
          showFileList={false}
          icon="aiFile"
          className={uploadCardStyle}
          style={style}
        >
          {trailing && (
            <Flex justify="flex-end" className={trailingStyle}>
              {trailing}
            </Flex>
          )}
        </UploadCard>
      </Tooltip>

      {hasUploadedFiles &&
        uploadedFiles.map((file) => (
          <FileRow
            key={file.id}
            file={file}
            loading={loading}
            onView={onView}
            onDelete={onDelete}
            onRetry={onRetry}
          />
        ))}
    </Flex>
  );
};
